import { Injectable, signal } from '@angular/core';
import { updatePreset } from '@primeuix/themes';

const DEFAULT_PRIMARY = '#2196F3';

/**
 * Gera a paleta primária 50..950 a partir de um hex base (500) e aplica em runtime.
 * É isso que permite cada loja ter a própria cor sem rebuild.
 */
@Injectable({ providedIn: 'root' })
export class ThemeService {
  readonly primaryColor = signal<string>(DEFAULT_PRIMARY);
  readonly dark = signal<boolean>(false);

  applyPrimary(hex: string): void {
    if (!hex) return;
    this.primaryColor.set(hex);
    updatePreset({ semantic: { primary: this.buildPalette(hex) } });
  }

  /** Restaura a cor padrão e desliga o modo escuro. Usar em telas fora do tenant logado (login, cadastro, landing). */
  reset(): void {
    this.applyPrimary(DEFAULT_PRIMARY);
    this.disableDark();
  }

  toggleDark(): void {
    this.dark.update((v) => !v);
    document.documentElement.classList.toggle('app-dark', this.dark());
  }

  /**
   * Troca de tema pelo botão: o novo tema avança do canto superior direito ao inferior esquerdo com a borda em meia-lua
   * (View Transitions API). Sem suporte no navegador ou com "reduzir movimento" ligado, troca direto.
   */
  switchDark(): void {
    const doc = document as Document & { startViewTransition?: (cb: () => void) => unknown };
    const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    if (!doc.startViewTransition || reduce) {
      this.toggleDark();
      return;
    }
    doc.startViewTransition(() => this.toggleDark());
  }

  /** Desliga o modo escuro sem mexer na cor. Usar em telas públicas (ex.: booking) que não devem herdar o dark mode da área logada. */
  disableDark(): void {
    this.dark.set(false);
    document.documentElement.classList.remove('app-dark');
  }

  private buildPalette(hex: string): Record<number, string> {
    const [r, g, b] = this.hexToRgb(hex);
    const stops: Record<number, number> = {
      50: 0.85, 100: 0.7, 200: 0.5, 300: 0.3, 400: 0.15, 500: 0,
      600: -0.1, 700: -0.2, 800: -0.3, 900: -0.45, 950: -0.6,
    };
    const out: Record<number, string> = {};
    for (const [stop, amt] of Object.entries(stops)) {
      out[+stop] = amt >= 0
        ? this.rgbToHex(this.mix([r, g, b], [255, 255, 255], amt))
        : this.rgbToHex(this.mix([r, g, b], [0, 0, 0], -amt));
    }
    return out;
  }

  private mix(c: number[], t: number[], amt: number): number[] {
    return c.map((v, i) => Math.round(v + (t[i] - v) * amt));
  }
  private hexToRgb(h: string): number[] {
    const n = parseInt(h.replace('#', ''), 16);
    return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
  }
  private rgbToHex([r, g, b]: number[]): string {
    return '#' + [r, g, b].map((v) => v.toString(16).padStart(2, '0')).join('');
  }
}
